import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { FuelData, FuelLayer, RegisterFuelPricePayload } from '@/domain/protocols/Fuel'
import { Result } from '@/domain/protocols/Result'

import { IFuelRepository } from './IFuelRepository'

import type { IHttpClient } from '@/infra/http/IHttpClient'

@injectable()
export class FuelRepository implements IFuelRepository {
  private static readonly REFRESH_AFTER_WRITE_DELAY_MS = 3000
  private fuelData: FuelData[] = []

  constructor(@inject(InjectionTokens.HttpClient) private httpClient: IHttpClient) {}

  protected getProvidersUrl(): string {
    return `${import.meta.env.VITE_BFF_URL}/fuel/providers`
  }

  protected getPricesUrl(): string {
    return `${import.meta.env.VITE_BFF_URL}/fuel/prices`
  }

  async init(): Promise<Result<void>> {
    return this.refresh()
  }

  async refresh(): Promise<Result<void>> {
    return this._loadFuelData()
  }

  private async _loadFuelData(): Promise<Result<void>> {
    try {
      const response = await this.httpClient.get<FuelData[]>(this.getProvidersUrl())

      if (response.success === false || response.status >= 300 || response.status < 200) {
        return Result.fail((response as any).error || 'Request failed')
      }

      this.fuelData = response.response.map((item) => this._normalizeFuelData(item))
      return Result.ok()
    } catch (error) {
      return Result.fail(error as Error)
    }
  }

  private _parsePhone(value?: string | string[]): string[] {
    if (Array.isArray(value)) {
      return value.map((phone) => String(phone ?? '').trim()).filter((phone) => phone.length > 0)
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((phone) => phone.trim())
        .filter((phone) => phone.length > 0)
    }

    return []
  }

  private _normalizeFuelData(item: FuelData): FuelData {
    return {
      ...item,
      phone: this._parsePhone(item.phone as string[] | string)
    }
  }

  private async _refreshAfterWrite(): Promise<void> {
    await new Promise((resolve) => {
      setTimeout(resolve, FuelRepository.REFRESH_AFTER_WRITE_DELAY_MS)
    })

    const refreshResult = await this.refresh()

    if (refreshResult.isFailure) {
      console.error('FuelRepository refresh after write failed:', refreshResult.error)
    }
  }

  getAllFuel(): Result<FuelData[]> {
    return Result.ok(this.fuelData)
  }

  getFuelByIcao(icao: string, layer?: FuelLayer): Result<FuelData[]> {
    const normalized = icao.trim().toUpperCase()
    let filtered = this.fuelData.filter((fuel) => fuel.adhpCode?.toUpperCase() === normalized)

    if (layer) {
      filtered = filtered.filter((fuel) => (layer === 'avgas' ? fuel.avgasProvider : fuel.jetProvider))
    }

    return Result.ok(filtered)
  }

  getProviders(): Result<string[]> {
    const providers = [...new Set(this.fuelData.map((fuel) => fuel.provider).filter(Boolean))]
    return Result.ok(providers)
  }

  async registerFuelPrice(payload: RegisterFuelPricePayload): Promise<Result<void>> {
    try {
      const response = await this.httpClient.post(this.getPricesUrl(), payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.success === false || response.status >= 300 || response.status < 200) {
        return Result.fail((response as any).error || 'Request failed')
      }

      void this._refreshAfterWrite()
      return Result.ok()
    } catch (error) {
      return Result.fail(error as Error)
    }
  }

  async registerProvider(payload: FuelData): Promise<Result<void>> {
    try {
      const response = await this.httpClient.post(this.getProvidersUrl(), payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.success === false || response.status >= 300 || response.status < 200) {
        return Result.fail((response as any).error || 'Request failed')
      }

      void this._refreshAfterWrite()
      return Result.ok()
    } catch (error) {
      return Result.fail(error as Error)
    }
  }
}
